import React, { useEffect, useState } from "react";
import { Box, Drawer, IconButton, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import save from "../../../../assets/save.svg";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";

const LOCAL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Date picker value (local calendar day) → display in drawer */
const toLocalDateInputValue = (value) => {
  if (!value) return "";
  if (LOCAL_DATE_REGEX.test(value)) return value;
  const local = convertUTCToLocalDateTime(value, { format: "YYYY-MM-DD" });
  return local === "—" ? "" : local;
};

/** Local date range → UTC instants for API `from` / `to` */
const buildFiltersForApi = (local) => {
  const { startDate, endDate, ...rest } = local;
  return {
    ...rest,
    startDate: startDate
      ? convertLocalDateTimeToUTC(`${startDate}T00:00:00`) || ""
      : "",
    endDate: endDate
      ? convertLocalDateTimeToUTC(`${endDate}T23:59:59`) || ""
      : "",
  };
};

const FilterDrawer = ({ open, onClose, filters, setFilters, onApplyFilters }) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);

  // 🔹 Priority options with translations
  const priorityOptions = [
    { key: "high", label: t("priorityOptions.high"), value: "high" },
    { key: "medium", label: t("priorityOptions.medium"), value: "medium" },
    { key: "low", label: t("priorityOptions.low"), value: "low" },
    { key: "urgent", label: t("priorityOptions.urgent"), value: "urgent" },
  ];

  // 🔹 Status options
  const statusOptions = [
    { key: "assigned", label: "Assigned", value: "assigned" },
    { key: "in_progress", label: "In Progress", value: "in_progress" },
    { key: "completed", label: "Completed", value: "completed" },
 
    { key: "cancelled", label: "Cancelled", value: "cancelled" },
  ];

  useEffect(() => {
    if (!open) return;
    setLocalFilters({
      ...filters,
      startDate: toLocalDateInputValue(filters.startDate),
      endDate: toLocalDateInputValue(filters.endDate),
    });
  }, [open, filters]);

  const apply = () => {
    const apiFilters = buildFiltersForApi(localFilters);
    console.log("✅ Applying filters:", apiFilters);
    onApplyFilters(apiFilters);
    onClose();
  };

  const clear = () => {
    const cleared = { priority: "", status: "", startDate: "", endDate: "" };
    console.log("♻️ Clearing filters:", cleared);
    setLocalFilters(cleared);
    onApplyFilters(cleared);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 550 },
          backgroundColor: "#E7EBEE",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#E7EBEE",
          zIndex: 10,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
            {t("filterDrawer.filterTask") || "Filter Tasks"}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            px: 3,
            py: 3,
            height: "calc(100vh - 90px)",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Priority Filter */}
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 500,
              color: "#2C384C",
              textAlign: "left",
              pt: 3,
            }}
          >
            {t("filterDrawer.priority") || "Priority"}
          </CustomText>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {priorityOptions.map((opt) => {
              const isSelected = localFilters.priority === opt.value;
              return (
                <Button
                  key={opt.key}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newPriority = isSelected ? "" : opt.value;
                    console.log("🔘 Priority selected:", newPriority);
                    setLocalFilters((prev) => ({
                      ...prev,
                      priority: newPriority,
                    }));
                  }}
                  sx={{
                    borderRadius: "6px",
                    fontSize: "15px",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    py: 0.8,
                    px: 2,
                    color: isSelected ? "#006EC2" : "#363333",
                    border: isSelected
                      ? "2px solid #006EC2"
                      : "2px solid #091E4224",
                    backgroundColor: "transparent",
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Box>

          {/* Status Filter */}
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 500,
              color: "#2C384C",
              textAlign: "left",
              pt: 3,
            }}
          >
            {t("filterDrawer.status") || "Status"}
          </CustomText>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {statusOptions.map((opt) => {
              const isSelected = localFilters.status === opt.value;
              return (
                <Button
                  key={opt.key}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newStatus = isSelected ? "" : opt.value;
                    console.log("🔘 Status selected:", newStatus);
                    setLocalFilters((prev) => ({
                      ...prev,
                      status: newStatus,
                    }));
                  }}
                  sx={{
                    borderRadius: "6px",
                    fontSize: "15px",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    py: 0.8,
                    px: 2,
                    color: isSelected ? "#006EC2" : "#363333",
                    border: isSelected
                      ? "2px solid #006EC2"
                      : "2px solid #091E4224",
                    backgroundColor: "transparent",
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Box>

          {/* Date From */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">
              {t("filterDrawer.startDate") || "Start Date"}
            </label>
            <input
              type="date"
              value={localFilters.startDate || ""}
           
                  onChange={(e) => {
      let val = e.target.value;
      const parts = val.split("-");
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].slice(0, 4); // restrict to 4 digits for year
        val = parts.join("-");
      }
      const validFormat = /^\d{0,4}(-\d{0,2}){0,2}$/;
      if (validFormat.test(val)) {
        setLocalFilters((prev) => ({
          ...prev,
          startDate: val,
        }));
      }
    }}
              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* Date To */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">
              {t("filterDrawer.endDate") || "End Date"}
            </label>
            <input
              type="date"
              value={localFilters.endDate || ""}
       

                  onChange={(e) => {
      let val = e.target.value;
      const parts = val.split("-");
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].slice(0, 4);
        val = parts.join("-");
      }
      const validFormat = /^\d{0,4}(-\d{0,2}){0,2}$/;
      if (validFormat.test(val)) {
        setLocalFilters((prev) => ({
          ...prev,
          endDate: val,
        }));
      }
    }}

              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* Buttons */}
          <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", gap: 2 }}>
            <CustomButton
              onClick={apply}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={save} alt="save" style={{ width: 16, height: 16 }} />
                  {t("filterDrawer.apply") || "Apply"}
                </Box>
              }
              size="medium"
            />
            <Button
              variant="outlined"
              onClick={clear}
              sx={{
                fontSize: 15,
                textTransform: "none",
                borderRadius: "5px",
                fontFamily: "Poppins, sans-serif",
                color: "#006EC2",
                border: "2px solid #006EC2",
                px: 3,
                py: 1,
                height: "fit-content",
              }}
            >
              {t("filterDrawer.clear") || "Clear"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
